import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, UserPlus, MapPin, Clock, CheckCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, required, hint, children }) => (
  <div className="flex flex-col">
    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 leading-tight min-h-[28px] flex items-end">
      <span>{label}{required && <span className="text-red-400 ml-0.5">*</span>}</span>
    </label>
    {children}
    {hint && <p className="text-[10px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

const InputField = ({ testId, ...props }) => (
  <input data-testid={testId} {...props}
    className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] transition-all" />
);

const SelectField = ({ testId, options, placeholder, ...props }) => (
  <select data-testid={testId} {...props}
    className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] transition-all bg-white">
    <option value="">{placeholder || 'Choisir...'}</option>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

const DAYS = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi', label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' },
];

const DEFAULT_HORAIRES = Object.fromEntries(
  DAYS.map(d => [d.key, d.key === 'dimanche'
    ? { ouvert: false, matin_debut: '', matin_fin: '', aprem_debut: '', aprem_fin: '' }
    : d.key === 'samedi'
      ? { ouvert: true, matin_debut: '09:00', matin_fin: '12:30', aprem_debut: '14:00', aprem_fin: '19:00' }
      : { ouvert: true, matin_debut: '09:00', matin_fin: '12:30', aprem_debut: '14:00', aprem_fin: '19:30' }
  ])
);

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressRef = useRef(null);

  const [form, setForm] = useState({
    prenom: '', nom: '', email: '', password: '', phone: '',
    company_name: '', sector_slug: 'pharmacie', plan: 'pilotage',
    adresse_complete: '', code_postal: '', city: '', latitude: 0, longitude: 0,
    zone_type: '', anciennete: '', employees_fte: '',
    surface_m2: '', is_franchise: false, mode_gestion: '',
    horaires: JSON.stringify(DEFAULT_HORAIRES),
    // Pharmacy
    groupement: '', groupement_autre: '',
    surface_vente_m2: '', surface_stockage_m2: '',
    lgo: '', lgo_autre: '',
    annee_installation_titulaire: '', nb_caisses: '',
    robot_delivrance: false, espace_preparation: false,
    espace_conseil: false, drive_click_collect: false,
    // Pharmacy commercial
    nb_medecins_prescripteurs: '', taux_tiers_payant_habituel: '',
    loyer_mensuel_ht: '', stock_ouverture_exercice: '',
    carte_fidelite: false, livraison_domicile: false,
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const isPharmacy = form.sector_slug === 'pharmacie';
  const totalSteps = isPharmacy ? 6 : 4;

  // Horaires helpers
  const horaires = JSON.parse(form.horaires);
  const setHoraire = (day, field, value) => {
    const h = { ...horaires, [day]: { ...horaires[day], [field]: value } };
    set('horaires', JSON.stringify(h));
  };

  const calcWeeklyHours = useCallback(() => {
    const h = JSON.parse(form.horaires);
    let totalMin = 0;
    Object.values(h).forEach(d => {
      if (!d.ouvert) return;
      ['matin', 'aprem'].forEach(slot => {
        const deb = d[`${slot}_debut`];
        const fin = d[`${slot}_fin`];
        if (deb && fin) {
          const [h1, m1] = deb.split(':').map(Number);
          const [h2, m2] = fin.split(':').map(Number);
          totalMin += (h2 * 60 + m2) - (h1 * 60 + m1);
        }
      });
    });
    return Math.round(totalMin / 60 * 10) / 10;
  }, [form.horaires]);

  // Address autocomplete
  const searchAddress = async (query) => {
    if (query.length < 4) { setAddressSuggestions([]); return; }
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`);
      const data = await res.json();
      setAddressSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch { setAddressSuggestions([]); }
  };

  const selectAddress = (feature) => {
    const p = feature.properties;
    const [lng, lat] = feature.geometry.coordinates;
    setForm(prev => ({
      ...prev,
      adresse_complete: p.label || '',
      code_postal: p.postcode || '',
      city: p.city || '',
      latitude: lat, longitude: lng,
    }));
    setShowSuggestions(false);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (addressRef.current && !addressRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSubmit = async () => {
    if (step !== totalSteps) return;
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        employees_fte: parseFloat(form.employees_fte) || 0,
        surface_m2: parseFloat(form.surface_m2) || 0,
        surface_vente_m2: parseFloat(form.surface_vente_m2) || 0,
        surface_stockage_m2: parseFloat(form.surface_stockage_m2) || 0,
        nb_caisses: parseInt(form.nb_caisses) || 0,
        nb_medecins_prescripteurs: parseInt(form.nb_medecins_prescripteurs) || 0,
        taux_tiers_payant_habituel: parseFloat(form.taux_tiers_payant_habituel) || 0,
        loyer_mensuel_ht: parseFloat(form.loyer_mensuel_ht) || 0,
        stock_ouverture_exercice: parseFloat(form.stock_ouverture_exercice) || 0,
        opening_hours_week: calcWeeklyHours(),
      };
      const res = await fetch(`${API_URL}/auth/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur lors de l\'inscription');
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const stepLabels = isPharmacy
    ? ['Offre', 'Identit\u00e9', '\u00c9tablissement', 'Horaires', 'Profil officine', 'Commercial']
    : ['Offre', 'Identit\u00e9', '\u00c9tablissement', 'Horaires'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-black text-[#1B2A4A] tracking-tight">BENCHORY</h1>
          <p className="text-sm text-gray-400 mt-1">Inscription dirigeant</p>
        </div>

        {/* Success confirmation screen */}
        {success ? (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center" data-testid="register-success">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#EAF3DE] flex items-center justify-center">
              <CheckCircle size={32} className="text-[#639922]" />
            </div>
            <h2 className="text-xl font-bold text-[#1B2A4A] mb-3">Demande enregistrée</h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-2">
              Merci <strong>{form.prenom}</strong>, votre demande d'inscription a bien été prise en compte.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              Notre équipe examine votre dossier et vous confirmera votre accès <strong>sous 24 heures</strong> par email à l'adresse <strong>{form.email}</strong>.
            </p>
            <div className="bg-[#F8F9FA] rounded-xl p-4 mb-6 text-left">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Récapitulatif</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-400">Dirigeant</span><span className="font-medium text-[#1B2A4A]">{form.prenom} {form.nom}</span></div>
                <div className="flex justify-between"><span className="text-gray-400">Établissement</span><span className="font-medium text-[#1B2A4A]">{form.company_name}</span></div>
                {form.city && <div className="flex justify-between"><span className="text-gray-400">Ville</span><span className="font-medium text-[#1B2A4A]">{form.city}</span></div>}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full h-11 bg-[#1B2A4A] text-white rounded-lg text-sm font-semibold hover:bg-[#243556] transition-all"
              data-testid="register-go-login"
            >
              Retour à la connexion
            </button>
          </div>
        ) : (
        <>
        {/* Progress */}
        <div className="flex items-center gap-1 mb-6 px-2">
          {stepLabels.map((label, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1 rounded-full transition-all ${i + 1 <= step ? 'bg-[#1B2A4A]' : 'bg-gray-200'}`} />
              <p className={`text-[9px] mt-1 text-center font-medium ${i + 1 <= step ? 'text-[#1B2A4A]' : 'text-gray-300'}`}>{label}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" data-testid="register-error">{error}</div>}

          <div>
            {/* Step 1: Plan Selection */}
            {step === 1 && (
              <div className="space-y-5" data-testid="register-step-plan">
                <p className="text-sm text-gray-500 mb-2">Choisissez l'offre qui correspond le mieux a votre besoin</p>
                <div className="space-y-3">
                  {[
                    { id: 'diagnostic', name: 'Diagnostic', price: '99', kpis: 8,
                      desc: 'Les indicateurs essentiels pour piloter votre rentabilit\u00e9',
                      highlights: ['Marge brute', 'Co\u00fbt \u00e9quipe', 'R\u00e9sultat courant', 'Panier moyen', 'Rotation stock', 'Tr\u00e9sorerie'] },
                    { id: 'pilotage', name: 'Pilotage', price: '179', kpis: 14, recommended: true,
                      desc: 'Analyse approfondie avec suivi de tendances et alertes',
                      highlights: ['Tout le Diagnostic', '+ Exposition caisses', '+ Fid\u00e9lit\u00e9 patients', '+ Taux g\u00e9n\u00e9riques', '+ Refus tiers payant', '+ Cash bloqu\u00e9 CPAM', '+ Force de conseil'] },
                    { id: 'premium', name: 'Premium', price: '249', kpis: 18,
                      desc: 'Vision exhaustive avec tous les leviers de performance',
                      highlights: ['Tout le Pilotage', '+ Rendement \u00e9quipe', '+ Productivit\u00e9 horaire', '+ Capital stock/m\u00b2', '+ CA au m\u00b2'] },
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`plan-select-${p.id}`}
                      onClick={() => set('plan', p.id)}
                      className={`w-full text-left rounded-xl border-2 p-4 transition-all ${form.plan === p.id ? 'border-[#1B2A4A] bg-[#1B2A4A]/[0.02] shadow-md' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${form.plan === p.id ? 'border-[#1B2A4A] bg-[#1B2A4A]' : 'border-gray-300'}`}>
                            {form.plan === p.id && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                          <span className="font-bold text-[#1B2A4A] text-sm">{p.name}</span>
                          {p.recommended && <span className="text-[9px] font-bold bg-[#B8860B] text-white px-2 py-0.5 rounded-full uppercase">Recommande</span>}
                          <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{p.kpis} KPIs</span>
                        </div>
                        <span className="text-lg font-black text-[#1B2A4A]">{p.price} <span className="text-xs font-normal text-gray-400">€/mois</span></span>
                      </div>
                      <p className="text-xs text-gray-500 mb-2 ml-7">{p.desc}</p>
                      <div className="flex flex-wrap gap-1.5 ml-7">
                        {p.highlights.map(h => (
                          <span key={h} className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{h}</span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Identity */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mb-4">Vos informations personnelles</p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Prénom" required>
                    <InputField testId="register-prenom" value={form.prenom} onChange={e => set('prenom', e.target.value)} />
                  </Field>
                  <Field label="Nom" required>
                    <InputField testId="register-nom" value={form.nom} onChange={e => set('nom', e.target.value)} />
                  </Field>
                </div>
                <Field label="Email professionnel" required>
                  <InputField testId="register-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                </Field>
                <Field label="Mot de passe" required>
                  <InputField testId="register-password" type="password" value={form.password} onChange={e => set('password', e.target.value)} />
                </Field>
                <Field label="Téléphone">
                  <InputField testId="register-phone" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} />
                </Field>
                <Field label="Secteur d'activité" required>
                  <SelectField testId="register-sector" value={form.sector_slug} onChange={e => set('sector_slug', e.target.value)}
                    options={[
                      { value: 'pharmacie', label: 'Pharmacie' },
                      { value: 'restaurant', label: 'Restaurant' },
                      { value: 'boulangerie', label: 'Boulangerie' },
                    ]} />
                </Field>
              </div>
            )}

            {/* Step 3: Establishment */}
            {step === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mb-4">Votre établissement</p>
                <Field label="Nom de l'établissement" required>
                  <InputField testId="register-company" value={form.company_name} onChange={e => set('company_name', e.target.value)} />
                </Field>

                {/* Address autocomplete */}
                <Field label="Adresse complète" required>
                  <div ref={addressRef} className="relative">
                    <div className="relative">
                      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        data-testid="register-address"
                        className="w-full h-10 pl-9 pr-3 rounded-lg border border-gray-200 text-sm text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20 focus:border-[#1B2A4A] transition-all"
                        value={form.adresse_complete}
                        onChange={e => { set('adresse_complete', e.target.value); searchAddress(e.target.value); }}
                        placeholder="Rechercher une adresse..."
                      />
                    </div>
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {addressSuggestions.map((f, i) => (
                          <button key={i} type="button" onClick={() => selectAddress(f)}
                            className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-[#1B2A4A]/5 transition-all border-b border-gray-50 last:border-0">
                            <span className="font-medium">{f.properties.name}</span>
                            <span className="text-gray-400 ml-1">{f.properties.postcode} {f.properties.city}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Field>

                {form.city && (
                  <div className="flex gap-3 text-xs text-gray-400 bg-gray-50 rounded-lg p-2.5">
                    <span>Code postal : <strong className="text-gray-600">{form.code_postal}</strong></span>
                    <span>Ville : <strong className="text-gray-600">{form.city}</strong></span>
                    {form.latitude !== 0 && <span className="text-emerald-500">Géolocalisé</span>}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Zone">
                    <SelectField testId="register-zone" value={form.zone_type} onChange={e => set('zone_type', e.target.value)}
                      options={[
                        { value: 'centre_ville', label: 'Centre-ville' },
                        { value: 'quartier', label: 'Quartier résidentiel' },
                        { value: 'zone_commerciale', label: 'Zone commerciale' },
                        { value: 'rural', label: 'Rural' },
                      ]} placeholder="Type de zone" />
                  </Field>
                  <Field label="Ancienneté">
                    <SelectField testId="register-anciennete" value={form.anciennete} onChange={e => set('anciennete', e.target.value)}
                      options={[
                        { value: '0-2', label: 'Moins de 2 ans' },
                        { value: '2-5', label: '2 à 5 ans' },
                        { value: '5-10', label: '5 à 10 ans' },
                        { value: '10-20', label: '10 à 20 ans' },
                        { value: '20+', label: 'Plus de 20 ans' },
                      ]} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Équivalents temps plein">
                    <InputField testId="register-fte" type="number" step="0.5" value={form.employees_fte} onChange={e => set('employees_fte', e.target.value)} placeholder="ex: 4.5" />
                  </Field>
                  <Field label="Mode de gestion">
                    <SelectField testId="register-mode-gestion" value={form.mode_gestion} onChange={e => set('mode_gestion', e.target.value)}
                      options={[
                        { value: 'titulaire_seul', label: 'Titulaire seul' },
                        { value: 'associes', label: 'Associés' },
                        { value: 'snc', label: 'SNC' },
                        { value: 'selarl', label: 'SELARL' },
                        { value: 'spfpl', label: 'SPFPL' },
                      ]} />
                  </Field>
                </div>
                <label className="flex items-center gap-2 cursor-pointer pt-1">
                  <input type="checkbox" checked={form.is_franchise} onChange={e => set('is_franchise', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#1B2A4A] focus:ring-[#1B2A4A]" data-testid="register-franchise" />
                  <span className="text-sm text-gray-600">Franchise</span>
                </label>
              </div>
            )}

            {/* Step 4: Opening hours */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-[#1B2A4A]" />
                  <p className="text-sm text-gray-500">Horaires d'ouverture</p>
                </div>
                <div className="space-y-2">
                  {DAYS.map(day => {
                    const d = horaires[day.key];
                    return (
                      <div key={day.key} className={`rounded-lg border p-3 transition-all ${d.ouvert ? 'border-[#1B2A4A]/15 bg-white' : 'border-gray-100 bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={d.ouvert}
                              onChange={e => setHoraire(day.key, 'ouvert', e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-[#1B2A4A] focus:ring-[#1B2A4A]" />
                            <span className={`text-sm font-semibold ${d.ouvert ? 'text-[#1B2A4A]' : 'text-gray-400'}`}>{day.label}</span>
                          </label>
                          {!d.ouvert && <span className="text-[10px] text-gray-400 uppercase">Fermé</span>}
                        </div>
                        {d.ouvert && (
                          <div className="grid grid-cols-4 gap-2">
                            <div>
                              <label className="text-[9px] text-gray-400 uppercase">Matin</label>
                              <input type="time" value={d.matin_debut} onChange={e => setHoraire(day.key, 'matin_debut', e.target.value)}
                                className="w-full h-8 px-1.5 rounded border border-gray-200 text-xs text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A]" />
                            </div>
                            <div>
                              <label className="text-[9px] text-gray-400 uppercase">à</label>
                              <input type="time" value={d.matin_fin} onChange={e => setHoraire(day.key, 'matin_fin', e.target.value)}
                                className="w-full h-8 px-1.5 rounded border border-gray-200 text-xs text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A]" />
                            </div>
                            <div>
                              <label className="text-[9px] text-gray-400 uppercase">Après-midi</label>
                              <input type="time" value={d.aprem_debut} onChange={e => setHoraire(day.key, 'aprem_debut', e.target.value)}
                                className="w-full h-8 px-1.5 rounded border border-gray-200 text-xs text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A]" />
                            </div>
                            <div>
                              <label className="text-[9px] text-gray-400 uppercase">à</label>
                              <input type="time" value={d.aprem_fin} onChange={e => setHoraire(day.key, 'aprem_fin', e.target.value)}
                                className="w-full h-8 px-1.5 rounded border border-gray-200 text-xs text-[#1B2A4A] focus:outline-none focus:border-[#1B2A4A]" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="bg-[#1B2A4A]/5 rounded-lg p-3 text-center">
                  <span className="text-xs text-gray-500">Volume hebdomadaire calculé : </span>
                  <span className="text-sm font-bold text-[#1B2A4A]">{calcWeeklyHours()} heures/semaine</span>
                </div>
              </div>
            )}

            {/* Step 5: Pharmacy structural profile */}
            {step === 5 && isPharmacy && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mb-2">Profil de votre officine</p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Groupement">
                    <SelectField testId="register-groupement" value={form.groupement} onChange={e => set('groupement', e.target.value)}
                      options={[
                        { value: 'welcopharm', label: 'Welcopharm' },
                        { value: 'phr', label: 'PHR' },
                        { value: 'giropharm', label: 'Giropharm' },
                        { value: 'cerp', label: 'CERP' },
                        { value: 'alphega', label: 'Alphega' },
                        { value: 'independant', label: 'Indépendant' },
                        { value: 'autre', label: 'Autre' },
                      ]} />
                  </Field>
                  {form.groupement === 'autre' ? (
                    <Field label="Précisez le groupement">
                      <InputField testId="register-groupement-autre" value={form.groupement_autre} onChange={e => set('groupement_autre', e.target.value)} />
                    </Field>
                  ) : (
                    <Field label="LGO utilisé">
                      <SelectField testId="register-lgo" value={form.lgo} onChange={e => set('lgo', e.target.value)}
                        options={[
                          { value: 'Winpharma', label: 'Winpharma' },
                          { value: 'Pharmagest', label: 'Pharmagest' },
                          { value: 'LGPI', label: 'LGPI' },
                          { value: 'Caduciel', label: 'Caduciel' },
                          { value: 'Alliadis', label: 'Alliadis' },
                          { value: 'Autre', label: 'Autre' },
                        ]} />
                    </Field>
                  )}
                </div>
                {form.groupement === 'autre' && (
                  <Field label="LGO utilisé">
                    <SelectField testId="register-lgo" value={form.lgo} onChange={e => set('lgo', e.target.value)}
                      options={[
                        { value: 'Winpharma', label: 'Winpharma' },
                        { value: 'Pharmagest', label: 'Pharmagest' },
                        { value: 'LGPI', label: 'LGPI' },
                        { value: 'Caduciel', label: 'Caduciel' },
                        { value: 'Alliadis', label: 'Alliadis' },
                        { value: 'Autre', label: 'Autre' },
                      ]} />
                  </Field>
                )}
                {form.lgo === 'Autre' && (
                  <Field label="Précisez le LGO">
                    <InputField testId="register-lgo-autre" value={form.lgo_autre} onChange={e => set('lgo_autre', e.target.value)} />
                  </Field>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Surface de vente (m²)">
                    <InputField testId="register-surface-vente" type="number" value={form.surface_vente_m2} onChange={e => set('surface_vente_m2', e.target.value)} />
                  </Field>
                  <Field label="Surface de stockage (m²)">
                    <InputField testId="register-surface-stockage" type="number" value={form.surface_stockage_m2} onChange={e => set('surface_stockage_m2', e.target.value)} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Année d'installation">
                    <InputField testId="register-annee-titulaire" type="number" value={form.annee_installation_titulaire} onChange={e => set('annee_installation_titulaire', e.target.value)} />
                  </Field>
                  <Field label="Nombre de caisses">
                    <InputField testId="register-nb-caisses" type="number" value={form.nb_caisses} onChange={e => set('nb_caisses', e.target.value)} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2">
                  {[
                    { key: 'robot_delivrance', label: 'Robot de délivrance' },
                    { key: 'espace_preparation', label: 'Espace de préparation' },
                    { key: 'espace_conseil', label: 'Espace conseil dédié' },
                    { key: 'drive_click_collect', label: 'Drive / Click & Collect' },
                  ].map(opt => (
                    <label key={opt.key} className="flex items-center gap-2 cursor-pointer py-1">
                      <input type="checkbox" checked={form[opt.key]} onChange={e => set(opt.key, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#1B2A4A] focus:ring-[#1B2A4A]" data-testid={`register-${opt.key}`} />
                      <span className="text-sm text-gray-600">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 6: Pharmacy commercial */}
            {step === 6 && isPharmacy && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 mb-2">Profil commercial et financier</p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Médecins prescripteurs (zone)">
                    <InputField testId="register-nb-medecins" type="number" value={form.nb_medecins_prescripteurs} onChange={e => set('nb_medecins_prescripteurs', e.target.value)} />
                  </Field>
                  <Field label="Taux tiers payant habituel (%)">
                    <InputField testId="register-taux-tp" type="number" step="0.1" value={form.taux_tiers_payant_habituel} onChange={e => set('taux_tiers_payant_habituel', e.target.value)} />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Loyer mensuel HT (euros)">
                    <InputField testId="register-loyer" type="number" value={form.loyer_mensuel_ht} onChange={e => set('loyer_mensuel_ht', e.target.value)} />
                  </Field>
                  <Field label="Stock ouverture exercice (euros)">
                    <InputField testId="register-stock-ouverture" type="number" value={form.stock_ouverture_exercice} onChange={e => set('stock_ouverture_exercice', e.target.value)} />
                  </Field>
                </div>
                <p className="text-[10px] text-gray-400">Valeur du stock au 1er jour de l'exercice comptable en cours</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-2">
                  {[
                    { key: 'carte_fidelite', label: 'Carte fidélité' },
                    { key: 'livraison_domicile', label: 'Livraison à domicile' },
                  ].map(opt => (
                    <label key={opt.key} className="flex items-center gap-2 cursor-pointer py-1">
                      <input type="checkbox" checked={form[opt.key]} onChange={e => set(opt.key, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#1B2A4A] focus:ring-[#1B2A4A]" data-testid={`register-${opt.key}`} />
                      <span className="text-sm text-gray-600">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6 gap-3">
              {step > 1 ? (
                <button type="button" onClick={() => setStep(step - 1)}
                  className="h-10 px-4 text-gray-500 text-sm font-medium hover:text-[#1B2A4A] transition-all flex items-center gap-1">
                  <ChevronLeft size={16} /> Retour
                </button>
              ) : (
                <Link to="/login" className="text-sm text-gray-400 hover:text-[#1B2A4A]">Déjà un compte ?</Link>
              )}

              {step < totalSteps ? (
                <button type="button" onClick={() => {
                  if (step === 1 && !form.plan) {
                    setError('Veuillez choisir une offre');
                    return;
                  }
                  if (step === 2 && (!form.prenom || !form.nom || !form.email || !form.password)) {
                    setError('Veuillez remplir les champs obligatoires');
                    return;
                  }
                  if (step === 3 && !form.company_name) {
                    setError("Veuillez saisir le nom de l'\u00e9tablissement");
                    return;
                  }
                  setError('');
                  setStep(step + 1);
                }}
                  className="h-10 bg-[#1B2A4A] text-white px-6 rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md flex items-center gap-1"
                  data-testid="register-next">
                  Suivant <ChevronRight size={16} />
                </button>
              ) : (
                <button type="button" disabled={loading}
                  onClick={handleSubmit}
                  className="h-10 bg-[#1B2A4A] text-white px-6 rounded-lg font-semibold text-sm hover:bg-[#152039] transition-all shadow-md disabled:opacity-50 flex items-center gap-2"
                  data-testid="register-submit">
                  {loading ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><UserPlus size={16} /> S'inscrire</>}
                </button>
              )}
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}
